/**
 * @since 2025-02-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { createContext, useContext } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Notifications.scss";

// Create a Context for notifications.
const NotificationContext = createContext();

// Store recent notifications to prevent duplicates
const recentNotifications = new Map();
const DUPLICATE_PREVENTION_TIME = 5000; // 5 seconds

export const Notifications = ({ children }) => {
  // Default options shared by all notifications.
  const defaultOptions = {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    pauseOnHover: false,
    closeOnClick: true,
    rtl: false,
    newestOnTop: true,
  };

  // Helper function to prevent duplicate notifications
  const preventDuplicates = (message, type, options) => {
    const key = `${type}:${message}`;
    const now = Date.now();
    
    // Check if we've shown this notification recently
    if (recentNotifications.has(key)) {
      const { timestamp, toastId } = recentNotifications.get(key);
      
      // If the notification was shown within the prevention time window
      if (now - timestamp < DUPLICATE_PREVENTION_TIME) {
        // Update the existing toast instead of creating a new one
        toast.update(toastId, { 
          ...options,
          render: message,
          autoClose: options.autoClose || defaultOptions.autoClose
        });
        
        // Update the timestamp
        recentNotifications.set(key, { timestamp: now, toastId });
        return toastId;
      }
    }
    
    // Create a new toast and store its ID
    const toastId = toast[type](message, options);
    recentNotifications.set(key, { timestamp: now, toastId });
    
    // Clean up old entries
    setTimeout(() => {
      const entry = recentNotifications.get(key);
      if (entry && entry.timestamp === now) {
        recentNotifications.delete(key);
      }
    }, DUPLICATE_PREVENTION_TIME);
    
    return toastId;
  };

  const notifySuccess = (message, options = {}) =>
    preventDuplicates(message, "success", { ...defaultOptions, ...options });

  const notifyError = (message, options = {}) =>
    preventDuplicates(message, "error", { ...defaultOptions, ...options });

  const notifyInfo = (message, options = {}) =>
    preventDuplicates(message, "info", { ...defaultOptions, ...options });

  const notifyWarning = (message, options = {}) =>
    preventDuplicates(message, "warning", { ...defaultOptions, ...options });

  return (
    <NotificationContext.Provider
      value={{ notifySuccess, notifyError, notifyInfo, notifyWarning }}
    >
      {children}
      <ToastContainer />
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);

export default Notifications;
